// 本地数据：家人、照片（含识别结果）、发型结果。全部存 IndexedDB，照片不出手机（除了生成时上传）。

import type { Gender } from "../../shared/styles";
import type { Pt } from "./geometry";
import type { Analysis } from "./vision";

export type Angle = "front" | "three-quarter" | "side";

export interface Member {
  id: string;
  name: string;
  gender: Gender;
  createdAt: number;
  /** 当前选中的照片 */
  photoId?: string;
  /** 首页头像用的结果 key（收藏的发型） */
  coverResultId?: string;
}

export interface StoredAnalysis {
  landmarks: Float32Array | null;
  mw: number;
  mh: number;
  hair: Uint8Array;
  faceSkin: Uint8Array;
  bodySkin: Uint8Array;
}

export interface Photo {
  id: string;
  memberId: string;
  angle: Angle;
  yaw: number;
  /** 裁剪 + 清理后的底图 */
  base: Blob;
  width: number;
  height: number;
  analysis: StoredAnalysis;
  createdAt: number;
}

export interface Result {
  /** `${photoId}|${styleKey}` */
  id: string;
  photoId: string;
  memberId: string;
  styleKey: string;
  styleId: string;
  colorId: string;
  custom?: string;
  /** 对齐后的生成图 */
  aligned: Blob;
  mask: Uint8Array;
  mw: number;
  mh: number;
  locked: boolean;
  /** 列表缩略图 */
  thumb: Blob;
  favorite: boolean;
  createdAt: number;
  duration: number;
}

const DB_NAME = "jianqian";
const DB_VERSION = 1;
let dbp: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (!dbp) {
    dbp = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains("members")) db.createObjectStore("members", { keyPath: "id" });
        if (!db.objectStoreNames.contains("photos")) {
          db.createObjectStore("photos", { keyPath: "id" }).createIndex("memberId", "memberId");
        }
        if (!db.objectStoreNames.contains("results")) {
          const s = db.createObjectStore("results", { keyPath: "id" });
          s.createIndex("photoId", "photoId");
          s.createIndex("memberId", "memberId");
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbp;
}

type StoreName = "members" | "photos" | "results";

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function store(name: StoreName, mode: IDBTransactionMode = "readonly") {
  const db = await open();
  return db.transaction(name, mode).objectStore(name);
}

export async function getAll<T>(name: StoreName): Promise<T[]> {
  return wrap((await store(name)).getAll()) as Promise<T[]>;
}

export async function get<T>(name: StoreName, id: string): Promise<T | undefined> {
  return wrap((await store(name)).get(id)) as Promise<T | undefined>;
}

export async function put<T>(name: StoreName, value: T): Promise<void> {
  await wrap((await store(name, "readwrite")).put(value));
}

export async function del(name: StoreName, id: string): Promise<void> {
  await wrap((await store(name, "readwrite")).delete(id));
}

export async function byIndex<T>(name: StoreName, index: string, key: string): Promise<T[]> {
  return wrap((await store(name)).index(index).getAll(key)) as Promise<T[]>;
}

/** 只取结果的元信息（不含大图），用于列表 */
export async function resultKeys(photoId: string): Promise<string[]> {
  const s = await store("results");
  return wrap(s.index("photoId").getAllKeys(photoId)) as Promise<string[]>;
}

export async function deleteMember(id: string): Promise<void> {
  const photos = await byIndex<Photo>("photos", "memberId", id);
  const results = await byIndex<Result>("results", "memberId", id);
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(["members", "photos", "results"], "readwrite");
    tx.objectStore("members").delete(id);
    for (const p of photos) tx.objectStore("photos").delete(p.id);
    for (const r of results) tx.objectStore("results").delete(r.id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deletePhoto(photo: Photo): Promise<void> {
  const keys = await resultKeys(photo.id);
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(["photos", "results"], "readwrite");
    tx.objectStore("photos").delete(photo.id);
    for (const k of keys) tx.objectStore("results").delete(k);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearResults(): Promise<void> {
  await wrap((await store("results", "readwrite")).clear());
}

export function uid(): string {
  return crypto.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export function packAnalysis(a: Analysis): StoredAnalysis {
  return {
    landmarks: a.landmarks ? Float32Array.from(a.landmarks.flat()) : null,
    mw: a.masks.w,
    mh: a.masks.h,
    hair: a.masks.hair,
    faceSkin: a.masks.faceSkin,
    bodySkin: a.masks.bodySkin,
  };
}

export function unpackAnalysis(s: StoredAnalysis): Analysis {
  let landmarks: Pt[] | null = null;
  if (s.landmarks) {
    landmarks = [];
    for (let i = 0; i < s.landmarks.length; i += 2) landmarks.push([s.landmarks[i], s.landmarks[i + 1]]);
  }
  return { landmarks, masks: { w: s.mw, h: s.mh, hair: s.hair, faceSkin: s.faceSkin, bodySkin: s.bodySkin } };
}

/** 申请持久化存储，避免浏览器清理时把照片删掉 */
export async function requestPersist(): Promise<boolean> {
  try {
    if (navigator.storage?.persisted && (await navigator.storage.persisted())) return true;
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}

export async function storageUsage(): Promise<{ usage: number; quota: number } | null> {
  try {
    const e = await navigator.storage?.estimate?.();
    return e ? { usage: e.usage ?? 0, quota: e.quota ?? 0 } : null;
  } catch {
    return null;
  }
}
